import {
  PackageType,
  type Offering,
  type Package,
} from "@revenuecat/purchases-js";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnalyticsEvent, track } from "../lib/analytics";
import { PRO_PRICE_MONTHLY, PRO_PRICE_YEARLY } from "../lib/pro";
import {
  loadProOffering,
  packagePrice,
  purchaseProPackage,
  resetProOfferingCache,
} from "../lib/purchases";
import { useAuthStore } from "../store/auth";
import { useSubscriptionStore } from "../store/subscription";
import { useUIStore } from "../store/ui";
import { Button } from "./ui/Button";
import { AnimatedCaliuLogo } from "./ui/AnimatedCaliuLogo";
import { ProTag } from "./ui/ProTag";
import { Icon, type IconId } from "@/components/ui/Icon";

// One Espurna icon per `pro.features` entry, in catalog order. Icons live in
// code so they stay identical across locales. Single source for every Pro
// surface (dialog + onboarding).
const FALLBACK_FEATURE_ICON: IconId = "heart";
const FEATURE_ICONS: IconId[] = [
  "infinity", // Unlimited notes
  "history", // Note version history
  "upload", // Export to PDF, HTML, RTF + bulk
  "brush", // All themes, fonts and app icons
  "heart", // Supports Caliu's development
];

/**
 * Logo + "Caliu Pro" lockup — the paywall hero, shared by the dialog and the
 * onboarding Pro step (mirrors the native `CaliuLogo` + `CaliuProLockup`).
 */
export function ProLockup() {
  return (
    <div className="flex flex-col items-center gap-4">
      {/* The logo is alive wherever Pro is offered: idle sway only. */}
      <AnimatedCaliuLogo size={56} className="text-accent" mode="idle" />
      <div className="flex items-center gap-2">
        <span className="font-headings text-2xl font-bold text-ink">Caliu</span>
        <ProTag className="text-base" />
      </div>
    </div>
  );
}

/**
 * The Pro value props as a bordered box (icon + label), straight from the
 * `pro.features` catalog. Shared by the dialog + onboarding (mirrors the native
 * `ProFeatureListBox`).
 *
 * The asterisked row is the one whose label promises something the web does
 * not have yet and the footnote names: PDF export (index 2). Keep the index
 * in step with `pro.features` and with `pro.webFootnote`. App icons (index 3)
 * are not on the web either, but the footnote no longer mentions them (owner's
 * call, 2026-10-13). The indices were wrong once already, pointing at "API access"
 * (which the web does have) after the list lost Drawing and Attachments, and
 * they shifted up by one again when the list lost its former second entry,
 * and back down when "Unlimited notes" took the first slot.
 */
export function ProFeatureListBox({ footnote = true }: { footnote?: boolean }) {
  const { t } = useTranslation();
  const features = t("pro.features", { returnObjects: true }) as string[];
  return (
    <div className="flex w-full flex-col gap-2.5">
      <div className="flex w-full flex-col gap-3.5 rounded-2xl border border-border-light bg-sidebar p-4 sm:p-5">
        {features.map((feature, i) => {
          const icon = FEATURE_ICONS[i] ?? FALLBACK_FEATURE_ICON;
          const webUnavailable = i === 2;
          return (
            <div key={feature} className="flex items-center gap-3">
              {/* shrink-0: a label that wraps would otherwise squeeze this
                  column, so every row's text started at a different x. */}
              <span className="flex w-6 shrink-0 justify-center text-accent">
                <Icon name={icon} size={16} />
              </span>
              <span className="text-[15px] font-medium text-ink">
                {feature}
                {webUnavailable && (
                  <sup className="ml-0.5 text-accent">{"*"}</sup>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {footnote && <ProWebFootnote />}
    </div>
  );
}

/** The "not on the web yet" footnote the feature asterisks point at. */
export function ProWebFootnote() {
  const { t } = useTranslation();
  return (
    <p className="text-center text-xs leading-relaxed text-muted">
      {"* "}
      {t("pro.webFootnote")}
    </p>
  );
}

/**
 * Highest price first: lifetime, then annual, then monthly, then anything
 * else. Same order as the native paywall. The preselected plan is
 * `defaultPackage`, not the first card.
 */
function rank(type: PackageType): number {
  if (type === PackageType.Lifetime) return 0;
  if (type === PackageType.Annual) return 1;
  if (type === PackageType.Monthly) return 2;
  return 3;
}

/**
 * The billed amount with its period ("$17.99 per year") — App Store parity
 * with the native paywall (3.1.2(c)): the price must be conspicuous wherever
 * the offer is presented, and the onboarding's pinned CTA can be on screen
 * with the plan cards scrolled away, so the disclosure line carries it.
 */
function billedAmount(pkg: Package, t: TFunction): string {
  const price = packagePrice(pkg);
  if (pkg.packageType === PackageType.Annual) return t("pro.perYear", { price });
  if (pkg.packageType === PackageType.Monthly) return t("pro.perMonth", { price });
  if (pkg.packageType === PackageType.Weekly) return t("pro.perWeek", { price });
  if (pkg.packageType === PackageType.Lifetime) return t("pro.oneTime", { price });
  return price;
}

/**
 * Free-trial length in days, or null when the package has no free trial. A
 * lifetime package never has one: Apple forbids introductory offers on
 * non-consumables, so the web must not advertise a trial the store cannot
 * match, whatever the product's phases say.
 */
function trialDays(pkg: Package): number | null {
  if (pkg.packageType === PackageType.Lifetime) return null;
  const period = pkg.webBillingProduct.freeTrialPhase?.period;
  if (!period || period.number <= 0) return null;
  const perUnit: Record<string, number> = {
    day: 1,
    week: 7,
    month: 30,
    year: 365,
  };
  return period.number * (perUnit[period.unit] ?? 1);
}

/** The preselected plan: annual (the value plan) when offered, else the first card. */
function defaultPackage(packages: Package[]): Package | null {
  return packages.find((p) => p.packageType === PackageType.Annual) ?? packages[0] ?? null;
}

function sortPackages(offering: Offering | null): Package[] {
  return [...(offering?.availablePackages ?? [])].sort(
    (a, b) => rank(a.packageType) - rank(b.packageType),
  );
}

/** State + actions behind every Pro purchase surface (see `useProPurchase`). */
export interface ProPurchaseState {
  loading: boolean;
  /** The live offering, for its dashboard metadata (lifetime launch price). */
  offering: Offering | null;
  packages: Package[];
  selected: Package | null;
  setSelected: (pkg: Package) => void;
  purchasing: boolean;
  handlePurchase: () => Promise<void>;
  /** Re-fetch the offering after it came back empty. */
  retry: () => void;
}

/**
 * The purchase engine shared by every Pro surface: offering load, plan
 * selection, and the buy action (with the silent backup activation for
 * device-only users). Split from the views so the onboarding overlay can keep
 * the plan cards in the scrolling step while pinning the CTA in its sticky
 * footer (mirrors the native coordinator + `ProPurchaseControls` split).
 *
 * `onPurchased` fires after RevenueCat confirms the entitlement (already
 * unlocked locally): the dialog closes; onboarding lets the `isPro` flip
 * re-render the step to its send-off.
 *
 * `onCheckoutChange` brackets RevenueCat's checkout (true while it is up). The
 * SDK mounts its checkout as a new node on `<body>`, outside any Radix dialog,
 * so a modal host must step aside for it: see `PaywallDialog`.
 */
export function useProPurchase(
  onPurchased: () => void,
  onCheckoutChange?: (open: boolean) => void,
): ProPurchaseState {
  const navigate = useNavigate();
  const confirmPurchase = useSubscriptionStore((s) => s.confirmPurchase);
  const paywallFeature = useUIStore((s) => s.paywallFeature);

  const [offering, setOffering] = useState<Offering | null>(null);
  const [selected, setSelected] = useState<Package | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Reuses the cache warmed on menu-open / onboarding entry, so this usually
    // resolves instantly.
    let cancelled = false;
    setLoading(true);
    void loadProOffering().then((o) => {
      if (cancelled) return;
      setOffering(o);
      setSelected(defaultPackage(sortPackages(o)));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const packages = sortPackages(offering);

  const retry = () => {
    resetProOfferingCache();
    setReloadKey((n) => n + 1);
  };

  const handlePurchase = async () => {
    // A subscription is owned by a Caliu account: `subscriptions.user_id` is
    // an FK to `users.id`, and RevenueCat's app_user_id IS that id. On the web
    // there is no offline entitlement to fall back on either (the SDK is never
    // configured without a user), so with no account there is nothing to bind
    // a receipt to. Send them to sign-in rather than selling into a void.
    //
    // Checked before `selected`: in local mode the SDK never configures, so
    // there is no offering and no selected package. This IS that CTA.
    if (!useAuthStore.getState().user) {
      useUIStore.getState().closePaywall();
      track(AnalyticsEvent.LocalModeUpgradePrompted, { source: "settings" });
      navigate("/register");
      return;
    }
    if (!selected) return;
    setPurchasing(true);
    const email = useAuthStore.getState().user?.email;
    onCheckoutChange?.(true);
    const ok = await purchaseProPackage(selected, email, paywallFeature);
    onCheckoutChange?.(false);
    setPurchasing(false);
    if (ok) {
      // RevenueCat already confirmed the `pro` entitlement: unlock instantly,
      // then have the server read the store itself (mirrors Swift's reconcile).
      void confirmPurchase();
      // A storage cap just moved, so attachments parked after three 402s are
      // worth another try. Dynamic import keeps the paywall out of the DB
      // module graph.
      void import("../lib/db").then((db) => db.retryFailedImageUploads());
      onPurchased();
    }
  };

  return {
    loading,
    offering,
    packages,
    selected,
    setSelected,
    purchasing,
    handlePurchase,
    retry,
  };
}

/**
 * The plan cards (live RevenueCat prices), with the loading spinner and the
 * no-Web-Billing static fallback. Selection writes back through the shared
 * `ProPurchaseState` so a pinned CTA elsewhere stays in sync.
 */
export function ProPlanCards({ purchase }: { purchase: ProPurchaseState }) {
  const { t } = useTranslation();
  const { loading, packages, selected, setSelected } = purchase;

  const planLabel = (type: PackageType): string => {
    if (type === PackageType.Annual) return t("pro.yearly");
    if (type === PackageType.Monthly) return t("pro.monthly");
    if (type === PackageType.Lifetime) return t("pro.lifetimePlan");
    return "Caliu Pro";
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Icon name="loader" size={24} className="animate-spin text-muted" />
      </div>
    );
  }

  if (packages.length === 0) {
    // No live offering: either local mode (the SDK only configures for a
    // signed-in user, so there is nothing to price) or a failed fetch. The
    // static prices still answer "what does it cost"; `ProPurchaseCta` carries
    // the way forward for both cases. The lifetime plan is deliberately NOT
    // listed here: paywalls only ever show what the RevenueCat offering
    // carries, so a price for a package that may not be on sale yet (before
    // day D, or after a rollback) must not come from a constant.
    return (
      <div className="text-center">
        {/* Each price stays whole: on a phone the line breaks at "or",
            never inside "$17.99 per year". */}
        <p className="flex flex-wrap items-baseline justify-center gap-x-2 text-[17px] font-bold text-ink">
          <span className="whitespace-nowrap">
            {t("pro.perMonth", { price: PRO_PRICE_MONTHLY })}
          </span>
          <span className="text-sm font-medium text-muted">
            {t("pro.or")}
          </span>
          <span className="whitespace-nowrap">
            {t("pro.perYear", { price: PRO_PRICE_YEARLY })}
          </span>
        </p>
      </div>
    );
  }

  // One row of equal tiles (three stacked cards no longer fit; mirrors the
  // native paywall). A tile carries the plan name, the billed price and a
  // muted caption (trial or one-time); the one-time wording lives in the
  // disclosure under the CTA (`ProPurchaseCta`), as on the native paywall.
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex gap-2.5 sm:gap-3.5">
        {packages.map((pkg) => {
          const isSelected = selected?.identifier === pkg.identifier;
          const days = trialDays(pkg);
          return (
            <button
              key={pkg.identifier}
              type="button"
              aria-pressed={isSelected}
              onClick={() => setSelected(pkg)}
              className={[
                "flex min-w-0 flex-1 flex-col items-center gap-1.5 rounded-2xl border-2 px-1 py-4 sm:py-5 transition-all duration-200",
                isSelected
                  ? "border-accent bg-accent-bg"
                  : "border-border-light bg-sidebar hover:border-border",
              ].join(" ")}
            >
              <span
                className={[
                  "max-w-full truncate text-sm font-semibold",
                  isSelected ? "text-accent" : "text-ink-secondary",
                ].join(" ")}
              >
                {planLabel(pkg.packageType)}
              </span>
              {/* The billed price stays the most conspicuous pricing element
                  (App Store 3.1.2(c), mirrored on the web). */}
              <span className="max-w-full truncate text-[15px] font-bold text-ink sm:text-[17px]">
                {packagePrice(pkg)}
              </span>
              {/* Subordinate to the billed price: the trial for a
                  subscription, the one-time nature for lifetime. */}
              <span className="line-clamp-3 text-center text-[10px] leading-4 text-muted sm:text-[11px]">
                {pkg.packageType === PackageType.Lifetime
                  ? t("pro.oneTimeNoRenewal")
                  : days != null && t("pro.freeTrial", { count: days })}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// `lg` is a fixed-height pill; a label that wraps (Catalan, German and
// Russian do on a phone) needs the pill to grow with it.
const CTA_CLASS = "mt-5 h-auto min-h-10 w-full py-2 text-center leading-snug";

/**
 * The buy CTA cluster: the purchase button and the billing disclosure.
 *
 * With no live offering it still leads somewhere. In local mode that is
 * sign-in (Pro binds to a Caliu account, so there is nothing to sell to a
 * device); otherwise the offering simply failed to load and the way out is to
 * try again. Renders nothing only while the offering is still loading.
 */
export function ProPurchaseCta({ purchase }: { purchase: ProPurchaseState }) {
  const { t } = useTranslation();
  const { loading, packages, selected, purchasing, handlePurchase, retry } = purchase;
  const localMode = useAuthStore((s) => s.user === null);
  const selectedLifetime = selected?.packageType === PackageType.Lifetime;

  if (loading) return null;

  if (packages.length === 0) {
    return (
      <div className="flex flex-col">
        <Button
          variant="primary"
          size="lg"
          className={CTA_CLASS}
          onClick={() => (localMode ? void handlePurchase() : retry())}
        >
          {localMode ? t("pro.signInToSubscribe") : t("common.tryAgain")}
        </Button>
        <p className="mx-auto mt-3 max-w-xs text-center text-xs leading-relaxed text-muted">
          {localMode ? t("pro.accountRequired") : t("pro.plansUnavailable")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Button
        variant="primary"
        size="lg"
        className={CTA_CLASS}
        disabled={!selected || purchasing}
        onClick={() => void handlePurchase()}
      >
        {purchasing ? (
          <Icon name="loader" size={16} className="animate-spin" />
        ) : selectedLifetime ? (
          t("pro.buy")
        ) : selected && trialDays(selected) != null ? (
          t("pro.startTrial")
        ) : (
          t("pro.continue")
        )}
      </Button>

      {/* Always names the billed amount; with a free trial it states that
          billing starts automatically afterwards (3.1.2(c): the amount
          dominates the trial wording even with the cards scrolled away). The
          lifetime plan gets its one-time wording: no auto-renew, no trial. */}
      {selected && (
        <p className="mx-auto mt-3 max-w-xs text-center text-xs leading-relaxed text-muted">
          {selectedLifetime
            ? t("pro.oneTimeDisclosure", { amount: packagePrice(selected) })
            : trialDays(selected) != null
              ? t("pro.billedSubtitle", {
                  count: trialDays(selected),
                  amount: billedAmount(selected, t),
                })
              : t("pro.autoRenew", { amount: billedAmount(selected, t) })}
        </p>
      )}
    </div>
  );
}

/**
 * The all-in-one purchase surface used by `PaywallDialog`: cards + CTA
 * stacked. Onboarding composes the same pieces itself so the CTA can live in
 * its sticky footer.
 */
export function ProPlanPurchase({
  onPurchased,
  onCheckoutChange,
}: {
  onPurchased: () => void;
  onCheckoutChange?: (open: boolean) => void;
}) {
  const purchase = useProPurchase(onPurchased, onCheckoutChange);
  return (
    <div className="flex flex-col">
      <ProPlanCards purchase={purchase} />
      <ProPurchaseCta purchase={purchase} />
    </div>
  );
}
